import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Cloud,
  Server,
  Phone,
  Radio,
  Voicemail,
  Sparkles,
  MessageSquare,
  Check,
  ArrowUpRight,
  Zap,
} from "lucide-react";
import { fadeUp, staggerParent } from "@/lib/motion";

const DEPLOY = [
  {
    icon: Cloud,
    title: "Hosted / Cloud PBX",
    desc: "We run it. You scale it. Zero on-site hardware required.",
    tint: "bg-lime-pop",
    bullets: [
      "Carrier-redundant SBC infrastructure",
      "Auto-failover across regions",
      "Predictable per-extension pricing",
      "Rapid provisioning of new sites",
    ],
  },
  {
    icon: Server,
    title: "On-Premises PBX",
    desc: "Full control, on your network — managed by our engineers.",
    tint: "bg-coral",
    bullets: [
      "Asterisk / FreePBX / 3CX engineering",
      "SIP trunk integration with any carrier",
      "Hardware sourcing & installation",
      "Optional remote management plans",
    ],
  },
];

const CAPABILITIES = [
  { icon: Phone, title: "Inbound & Outbound", body: "DID provisioning, number porting, and concurrent-call capacity sized to your peaks." },
  { icon: Radio, title: "Intelligent Routing", body: "Time-of-day, skills-based, follow-me, hunt groups, and overflow strategies." },
  { icon: Sparkles, title: "Auto Attendants & IVR", body: "Multi-level menus, business-hours scheduling, and dial-by-name directories." },
  { icon: Voicemail, title: "Voicemail Suite", body: "Voicemail-to-email, transcription, group mailboxes, shared inbox workflows." },
  { icon: MessageSquare, title: "SMS & Unified Inbox", body: "Send and receive SMS from business numbers in one threaded view." },
  { icon: Zap, title: "CRM Integrations", body: "Screen pops, click-to-dial, and call sync with your tools of record." },
];

export default function Services() {
  return (
    <div data-testid="services-page" className="overflow-hidden pt-24 sm:pt-32">
      <section className="relative py-16 sm:py-24 grain">
        <div className="container-x relative">
          <motion.div
            initial="hidden"
            animate="show"
            variants={staggerParent}
            className="max-w-4xl"
          >
            <motion.div variants={fadeUp} className="text-overline">
              Services
            </motion.div>
            <motion.h1
              variants={fadeUp}
              className="mt-4 font-display text-6xl sm:text-8xl text-ink tracking-[-0.04em] leading-[0.92]"
            >
              Every component your team needs to communicate — in one stack.
            </motion.h1>
            <motion.p
              variants={fadeUp}
              className="mt-8 text-lg sm:text-xl text-ink-2 leading-relaxed max-w-2xl"
            >
              From single-line offices to multi-site enterprises, we deploy
              the right phone platform — and stay on to support it.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* DEPLOYMENT CARDS */}
      <section className="py-16 sm:py-24 border-t border-ink">
        <div className="container-x">
          <div className="text-overline">Deployment models</div>
          <h2 className="mt-4 font-display text-4xl sm:text-6xl text-ink tracking-tight leading-[0.98] max-w-3xl">
            Hosted, on-prem, or hybrid — <em className="text-muted-warm not-italic">your call</em>.
          </h2>

          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-60px" }}
            variants={staggerParent}
            className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            {DEPLOY.map((opt, i) => {
              const Icon = opt.icon;
              return (
                <motion.div
                  key={opt.title}
                  variants={fadeUp}
                  whileHover={{ y: -8 }}
                  transition={{ duration: 0.35 }}
                  className={`${opt.tint} rounded-3xl border border-ink p-8 sm:p-10 text-ink`}
                  data-testid={`deploy-card-${i}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="h-12 w-12 rounded-full bg-ink text-white flex items-center justify-center">
                      <Icon className="h-5 w-5" />
                    </div>
                    <span className="font-mono text-xs opacity-70">0{i + 1}</span>
                  </div>
                  <h3 className="mt-8 font-display text-3xl sm:text-4xl tracking-tight">
                    {opt.title}
                  </h3>
                  <p className="mt-3 text-ink-2 opacity-90">{opt.desc}</p>
                  <ul className="mt-8 space-y-3">
                    {opt.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-3 text-sm">
                        <Check className="h-4 w-4 mt-0.5 shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* CAPABILITIES GRID */}
      <section className="py-20 sm:py-28 border-t border-ink bg-bone-2">
        <div className="container-x">
          <div className="text-overline">Capabilities</div>
          <h2 className="mt-4 font-display text-4xl sm:text-6xl text-ink tracking-tight leading-[0.98] max-w-3xl">
            Features that map to the way customers actually call.
          </h2>

          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-40px" }}
            variants={staggerParent}
            className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-ink rounded-3xl overflow-hidden border border-ink"
          >
            {CAPABILITIES.map((c) => {
              const Icon = c.icon;
              return (
                <motion.div
                  key={c.title}
                  variants={fadeUp}
                  className="bg-bone p-8 sm:p-10 hover:bg-lime-pop transition-colors group"
                >
                  <Icon className="h-6 w-6 text-ink" />
                  <h3 className="mt-6 font-display text-xl sm:text-2xl text-ink tracking-tight">
                    {c.title}
                  </h3>
                  <p className="mt-2 text-sm text-ink-2 leading-relaxed">
                    {c.body}
                  </p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 sm:py-32">
        <div className="container-x text-center max-w-3xl mx-auto">
          <h2 className="font-display text-4xl sm:text-6xl text-ink tracking-tight leading-[0.98]">
            Not sure which deployment fits?
          </h2>
          <p className="mt-5 text-lg text-ink-2 max-w-xl mx-auto">
            Send a quick inquiry and we'll recommend the right architecture for
            your team size, locations, and call volume.
          </p>
          <div className="mt-10">
            <Link
              to="/contact"
              className="btn-lime group text-base h-14 px-8"
              data-testid="services-cta-inquire"
            >
              Get a Recommendation
              <ArrowUpRight className="h-5 w-5 transition-transform group-hover:rotate-45" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
